import type { RenderedAsset } from '@brickup/shared/dto'
import type { CatalogueKind } from '@brickup/shared/enums'
import { CATALOGUE } from '@brickup/shared/locales/de/catalogue'
import { cn, Glyph, GLYPH_INK, type GlyphName } from '@/components/base'
import { ColorDot } from '@/components/ColorDot'
import { useIconPicker } from '@/components/Internal/IconPicker'
import { FaceImage } from '@/components/ProductSwatch'
import { resolveIcon } from '@/constants/icon-registry'

/** The mark of a kind, in the app's own solid set. */
const KIND_GLYPH = {
  MEASURE: 'measure',
  PRODUCT: 'product',
  LABOUR: 'labour',
  FORMULA: 'formula',
  // A term of a price: the same mark the formula branch wears, because what a
  // Preisbaustein holds is an amount, a percentage or an expression.
  PRICE_BLOCK: 'formula',
  // Several things carried as one — a mark of its own, because a folder is a
  // place entries are filed in and a Preisgruppe is a thing that charges.
  PRICE_GROUP: 'bundle',
} as const satisfies Record<CatalogueKind, GlyphName>

/**
 * Whatever an entry carries that a mark can be read off — one field per rung
 * of the order below.
 *
 * All optional because the three kinds carry different ones: a formula has
 * only an icon, a measure an icon and a picture, a product a colour as well.
 */
export type GlyphEntry = {
  icon?: string | null
  hex?: string | null
  cardImage?: RenderedAsset | null
}

/** What the mark is drawn from when the entry has no icon of its own. */
type FallbackSource = 'image' | 'color' | 'kind'

function fallbackSourceOf(entry: GlyphEntry): FallbackSource {
  if (entry.cardImage) return 'image'
  if (entry.hex) return 'color'
  return 'kind'
}

/**
 * The sentence the icon picker shows beside the button that clears an icon:
 * what the entry would wear instead.
 *
 * Read off the same rule the glyph renders by, so the promise and the mark
 * cannot disagree — a second statement of the order is how an entry came to
 * show a picture the picker said it would not.
 *
 * `undefined` where the entry carries nothing of its own: the picker then
 * states its own sentence, which is the same one every other picker shows.
 */
function iconFallbackHint(entry: GlyphEntry): string | undefined {
  const source = fallbackSourceOf(entry)
  return source === 'kind' ? undefined : CATALOGUE.iconFallback[source]
}

/**
 * The one mark that stands for a catalogue entry — in the tree row and on the pane
 * that opens from it, so a row and its pane cannot show different marks.
 *
 * A chosen icon wins: picking one is the operator saying what the entry should
 * wear, and an entry whose picture outranked it made the picker look broken.
 * Without one the entry's own first picture stands for it, then a product's
 * colour, then the kind's solid mark.
 */
export function CatalogueGlyph({
  kind,
  entry,
  size = 18,
}: {
  kind: CatalogueKind
  entry: GlyphEntry
  size?: number
}) {
  if (entry.icon) {
    const Chosen = resolveIcon(entry.icon)
    return <Chosen size={size} className={cn('shrink-0', GLYPH_INK)} />
  }

  // Exhaustive on the same value `iconFallbackHint` names, so the picker's
  // promise and the mark are one decision rather than two that agree.
  switch (fallbackSourceOf(entry)) {
    case 'image':
      // `fallbackSourceOf` answers `image` only where there is one; the
      // switch is on its answer, which cannot carry the narrowing with it.
      return <FaceImage image={entry.cardImage!} size={size} />
    case 'color':
      return <ColorDot hex={entry.hex} size={size} />
    case 'kind':
      return <Glyph name={KIND_GLYPH[kind]} size={size} />
  }
}

/**
 * The mark of the entry a pane is open on, and the dialog that changes it.
 *
 * All six branches put the same three things in their identity strip — the
 * mark, the press that opens the picker, and the dialog — and the promise the
 * picker makes about what the entry falls back to has to be read off the same
 * entry the mark is drawn from. Stated per pane, the six drifted: one drew its
 * mark two pixels smaller than the other five.
 *
 * Takes the entry as the pane holds it, which is `undefined` until the read
 * lands: the strip is drawn before there is anything to draw it from.
 */
export function useCatalogueMark({
  kind,
  entry,
  name,
  onChange,
}: {
  kind: CatalogueKind
  entry: GlyphEntry | null | undefined
  /** What the suggestions are drawn from — the entry's own display name. */
  name: string | null | undefined
  onChange: (icon: string | null) => void
}) {
  const mark = entry ?? {}
  const picker = useIconPicker({
    value: mark.icon ?? null,
    onChange,
    suggestFrom: name ?? undefined,
    emptyHint: iconFallbackHint(mark),
  })

  return {
    glyph: <CatalogueGlyph kind={kind} entry={mark} size={22} />,
    onGlyphClick: picker.open,
    dialog: picker.dialog,
  }
}
